import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "@hubertai/hubert-component-library";
import { fetchInvitation, sendBenefitsEmail } from "../../../behavior/api/invitation";
import Header from "../../../components/header/Header";
import CompletedAlert from "../../../components/interview/CompletedAlert";
import type { TPageState } from "../../../constants";
import { PageState } from "../../../constants";
import type { Invitation, InvitationTokenPayload } from "../../../types/invitation";
import InviteEmailSignIn from "./InviteEmailSignIn";
import "./LoginInviteEmail.css";

const LoginInviteEmailInterviewCompleted = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [invitation, setInvitation] = useState<Invitation>();
    const [pageState, setPageState] = useState<TPageState>(PageState.INVITATION_POST_INTERVIEW_SIGN_IN);
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState<string>();

    const verify = useCallback(
        async (token: string): Promise<void> => {
            try {
                const invitationData = await fetchInvitation(token);

                if (!invitationData) {
                    toast.error(t("AppMessages.server-error"));
                    return;
                }

                const type = invitationData.__typename ?? invitationData.type;
                if (type !== "Invitation") {
                    toast.error(t(`ProcessInvitation.${type}`));
                    navigate("/", { replace: true });
                    return;
                }

                if (invitationData.interviewCompleted) {
                    await sendBenefitsEmail({ token });
                    setInvitation(invitationData);
                } else {
                    toast.error(t("InterviewCompletedPageInviteEmail.InterviewNotCompleted"));
                    navigate("/", { replace: true });
                }
            } catch (e) {
                toast.error(t((e as Error).message));
            }
        },
        [navigate, t],
    );

    useEffect(() => {
        const stateParam = searchParams.get("state");
        if (stateParam) {
            const state = stateParam as TPageState;
            setPageState(state);
        }

        const token = searchParams.get("token");
        if (token) {
            const jwt = jwtDecode<InvitationTokenPayload>(token);
            setEmail(jwt?.email);
            verify(token);
        }
    }, [navigate, searchParams, verify]);

    return (
        <>
            <Header />
            <div className="login-interview-completed-invite-email-wrapper">
                <CompletedAlert />
                <InviteEmailSignIn invitation={invitation} state={pageState} email={email} />
            </div>
        </>
    );
};

export default LoginInviteEmailInterviewCompleted;
